import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { withMemberGate } from "@/lib/with-member-gate";
import {
  REGULATE_LINKS,
  SAFETY_NOTICE,
  WEEK_REFLECTION_DAYS,
  WEEK_REFLECTION_FIELDS,
  dayByNumber,
  phaseForDay,
} from "@/lib/secure-within";
import { SecureWithinProvider, useSecureWithin } from "@/components/secure-within/state";
import { PatternGuidance, SecureWithinBlock } from "@/components/secure-within/Blocks";

export const Route = createFileRoute("/secure-within/day/$day")({
  head: ({ params }) => {
    const day = dayByNumber(Number(params.day));
    const title = day
      ? `Day ${day.day}: ${day.title} — Secure Within | Recovery Trauma™`
      : "Secure Within | Recovery Trauma™";
    const description = day
      ? day.focus[0].slice(0, 155)
      : "A 30-day attachment healing journey.";
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "article" },
        { name: "twitter:card", content: "summary" },
        { name: "robots", content: "noindex" },
      ],
    };
  },
  component: withMemberGate(DayRoute, "Secure Within"),
});

function DayRoute() {
  return (
    <SecureWithinProvider>
      <DayPage />
    </SecureWithinProvider>
  );
}

function DayPage() {
  const { day: dayParam } = Route.useParams();
  const navigate = useNavigate();
  const dayNumber = Number(dayParam);
  const base = dayByNumber(dayNumber);
  const { loading, error, refetch, completedDays, markDay, overrides } = useSecureWithin();
  const override = overrides[`day-${dayNumber}`] ?? {};
  const day = base
    ? {
        ...base,
        title: override.title ?? base.title,
        focus: override.focus?.length ? override.focus : base.focus,
        pause: override.pause ?? base.pause,
        reflection: override.reflection ?? base.reflection,
        affirmation: override.affirmation ?? base.affirmation,
      }
    : undefined;
  const [busy, setBusy] = useState(false);
  const [readyForTender, setReadyForTender] = useState(false);

  if (!day) {
    return (
      <div className="mx-auto w-full max-w-xl px-4 py-16 text-center">
        <p>That stage isn't part of the journey.</p>
        <Button asChild className="mt-4 min-h-[48px]">
          <Link to="/secure-within">Back to Secure Within</Link>
        </Button>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="mx-auto w-full max-w-3xl space-y-4 px-4 py-10">
        <Skeleton className="h-8 w-40" />
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-72 w-full" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto w-full max-w-xl px-4 py-16 text-center">
        <p>Something didn't load correctly.</p>
        <Button className="mt-4 min-h-[48px]" onClick={refetch}>
          Try again
        </Button>
      </div>
    );
  }

  const phase = phaseForDay(day.day);
  const done = completedDays.includes(day.day);
  const showWeekReflection = WEEK_REFLECTION_DAYS.includes(day.day);

  const complete = async () => {
    setBusy(true);
    try {
      await markDay(day.day, !done);
      if (!done && day.day < 30) {
        void navigate({
          to: "/secure-within/day/$day",
          params: { day: String(day.day + 1) },
        });
      } else if (!done) {
        void navigate({ to: "/secure-within" });
      }
    } finally {
      setBusy(false);
    }
  };

  if (day.tender && !readyForTender && !done) {
    return (
      <div className="mx-auto w-full max-w-xl px-4 py-16">
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[var(--gold-deep)]">
          Day {day.day} of 30
        </p>
        <h1 className="mt-2 text-2xl font-semibold">
          {day.emoji} {day.title}
        </h1>
        <p className="mt-4 text-[15px] leading-relaxed text-foreground/85">{day.pause}</p>
        <p className="mt-3 text-sm text-muted-foreground">
          You can stop at any point, and nothing is lost. There's no rush to do this today.
        </p>
        <div className="mt-6 flex flex-col gap-3 sm:flex-row">
          <Button className="min-h-[48px]" onClick={() => setReadyForTender(true)}>
            I'm ready to begin
          </Button>
          <Button asChild variant="outline" className="min-h-[48px]">
            <Link to="/grounding">Ground first</Link>
          </Button>
          <Button asChild variant="ghost" className="min-h-[48px]">
            <Link to="/secure-within">Not today</Link>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-3xl px-4 pb-16 pt-8">
      <Link to="/secure-within" className="text-sm text-muted-foreground underline">
        <span className="inline-block rtl:rotate-180">←</span> Secure Within
      </Link>

      <p className="mt-4 text-xs font-semibold uppercase tracking-[0.18em] text-[var(--gold-deep)]">
        Day {day.day} of 30 · {phase.name}
      </p>
      <h1 className="mt-2 text-2xl font-semibold sm:text-3xl">
        {day.emoji} {day.title}
      </h1>

      <section className="mt-6 rounded-3xl border border-gold/25 bg-card p-5">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          Today's focus
        </h2>
        <div className="mt-3 space-y-3">
          {day.focus.map((p, i) => (
            <p key={i} className="text-[15px] leading-relaxed text-foreground/85">
              {p}
            </p>
          ))}
        </div>
      </section>

      <section className="mt-4 rounded-3xl border border-border bg-muted/40 p-5">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          Pause &amp; notice
        </h2>
        <p className="mt-2 text-[15px] text-foreground/85">{day.pause}</p>
      </section>

      {day.day >= 3 ? (
        <div className="mt-4">
          <PatternGuidance />
        </div>
      ) : null}

      <div className="mt-6 space-y-5">
        {day.blocks.map((block) => (
          <SecureWithinBlock key={block.key} block={block} day={day.day} />
        ))}
      </div>

      {day.regulate?.length ? (
        <section className="mt-6 rounded-3xl border border-border bg-background p-5">
          <h2 className="text-sm font-semibold">Feeling activated? Regulate before continuing.</h2>
          <div className="mt-3 flex flex-wrap gap-2">
            {day.regulate.map((key) => {
              const link = REGULATE_LINKS[key];
              return (
                <Button key={key} asChild variant="outline" className="min-h-[48px]">
                  <Link to={link.to as never}>
                    <span className="me-2">{link.emoji}</span>
                    {link.label}
                  </Link>
                </Button>
              );
            })}
          </div>
        </section>
      ) : null}

      {showWeekReflection ? (
        <div className="mt-6">
          <SecureWithinBlock
            block={{
              type: "questions",
              key: `sw-phase-reflection-${day.day}`,
              title: "Look how much you've explored",
              intro: "A gentle look back before the next phase.",
              fields: WEEK_REFLECTION_FIELDS,
            }}
            day={day.day}
          />
        </div>
      ) : null}

      <section className="mt-6 rounded-3xl border border-gold/25 bg-card p-5">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          Today's reflection
        </h2>
        <p className="mt-2 text-[15px] text-foreground/85">{day.reflection}</p>
        <p className="mt-4 rounded-2xl bg-gold-light/30 p-4 text-[15px] font-medium">
          Take this with you: {day.affirmation}
        </p>
      </section>

      <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
        <Button className="min-h-[48px]" onClick={complete} disabled={busy}>
          {done ? "✓ Explored — tap to undo" : busy ? "Saving…" : "✓ Complete stage"}
        </Button>
        <Button asChild variant="outline" className="min-h-[48px]">
          <Link to="/secure-within">Save &amp; continue later</Link>
        </Button>
        {day.day < 30 ? (
          <Button asChild variant="ghost" className="min-h-[48px]">
            <Link to="/secure-within/day/$day" params={{ day: String(day.day + 1) }}>
              Skip for now
            </Link>
          </Button>
        ) : null}
      </div>

      <p className="mt-4 text-xs text-muted-foreground">
        Your journey is still here whenever you're ready. Everything you write saves on its own.
      </p>

      <div className="mt-8 rounded-2xl border border-border bg-muted/40 p-4 text-xs leading-relaxed text-muted-foreground">
        <p>{SAFETY_NOTICE}</p>
        <p className="mt-2">
          If you need urgent support,{" "}
          <Link to="/crisis" className="underline">
            crisis help is here
          </Link>
          .
        </p>
      </div>
    </div>
  );
}
